import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import React, { useId, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import Checkbox from '../../common/Checkbox';
import { MODAL_TOOLTIP_STYLE } from '../../common/tooltipStyles';
import { AssignTranscriptSpeaker } from './AssignTranscriptSpeaker';

import type { UnmappedSpeakerOffer } from '@src/hooks/useUnmappedSpeakerApply';

/**
 * "Apply to {Speaker}'s Unmapped Scenes" (PLAT-11992), shared by the Creator
 * Studio and clip preview reframe modals. Shown only when the scene's speaker
 * has no face mapped and the crop holds an unmapped face. A transcript
 * speaker not assigned yet is assigned first — from the story, the showcase,
 * or a new speaker.
 */
export function UnmappedSpeakerCheckbox({
  offer,
  checked,
  onChange,
  onLinkSpeaker,
}: {
  offer: UnmappedSpeakerOffer;
  checked: boolean;
  onChange: (checked: boolean) => void;
  onLinkSpeaker: (speakerLabel: string, datoSpeakerId: string) => void;
}) {
  const [isAssigning, setIsAssigning] = useState(false);
  /** Per instance — a shared id fires every mounted tooltip off one hover. */
  const tooltipId = useId();
  // In a tooltip rather than the hint row: a line this long widens the
  // centred modal.
  const explanation = `Maps this face to ${offer.speakerName}, then reframes their scenes that weren't framed on them. Scenes framed by hand are kept.`;

  return (
    <>
      <Checkbox
        label={
          offer.isNamed
            ? `Apply to ${offer.speakerName}'s Unmapped Scenes`
            : `Assign ${offer.speakerName} and Apply to Their Scenes`
        }
        checked={checked}
        color={checked ? 'var(--accent)' : 'var(--background)'}
        borderColor={checked ? 'var(--accent)' : 'var(--border)'}
        onClick={() => {
          if (!checked && !offer.isNamed) {
            setIsAssigning(true);
            return;
          }
          onChange(!checked);
        }}
        overrides={{ containerStyles: { 'align-items': 'center' } }}
      />
      <InfoMarker
        data-tooltip-id={tooltipId}
        data-tooltip-content={explanation}
        aria-label={explanation}
      >
        <InfoIconSmall />
      </InfoMarker>
      <Tooltip
        id={tooltipId}
        style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '280px' }}
      />
      {isAssigning && (
        <AssignTranscriptSpeaker
          speakerLabel={offer.speakerLabel}
          speakerName={offer.speakerName}
          onClose={() => setIsAssigning(false)}
          onAssigned={datoSpeakerId => {
            setIsAssigning(false);
            onLinkSpeaker(offer.speakerLabel, datoSpeakerId);
            onChange(true);
          }}
        />
      )}
    </>
  );
}

const InfoMarker = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: calc(-1 * var(--spacing-xs));
  color: var(--text-muted);
  cursor: help;
`;
